import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, FileText, ImagePlus, Loader2, Save } from "lucide-react";
import api, { resolveImg } from "../api";
import RichTextEditor from "./RichTextEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";

export default function PostEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [published, setPublished] = useState(true);
  const [resetKey, setResetKey] = useState(0);

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const docxRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (editing) {
      api.get(`/posts/${id}`).then((r) => {
        const p = r.data;
        setTitle(p.title); setSubtitle(p.subtitle || "");
        setContentHtml(p.content_html || ""); setImageUrl(p.image_url || null);
        setPublished(p.published); setResetKey((k) => k + 1);
      }).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onDocx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setConverting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/convert-docx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContentHtml(r.data.html);
      setResetKey((k) => k + 1);
      if (!title) setTitle(file.name.replace(/\.docx$/i, ""));
      toast({ title: "Document imported", description: "Formatting converted \u2014 edit below as needed." });
    } catch (err) {
      toast({ title: "Import failed", description: err?.response?.data?.detail || "Could not read .docx", variant: "destructive" });
    } finally {
      setConverting(false);
      if (docxRef.current) docxRef.current.value = "";
    }
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(r.data.url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingImg(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const save = async () => {
    if (!title.trim()) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { title, subtitle, content_html: contentHtml, image_url: imageUrl, published };
    try {
      if (editing) await api.put(`/posts/${id}`, payload);
      else await api.post("/posts", payload);
      toast({ title: editing ? "Post updated" : "Post published" });
      navigate("/admin");
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#e63946]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="border-b border-[#26262a] sticky top-0 bg-[#0f0f10]/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-1.5 text-gray-300 hover:text-white text-sm"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <h1 className="text-lg font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>{editing ? "Edit post" : "New post"}</h1>
          <Button onClick={save} disabled={saving} className="bg-[#e63946] hover:bg-[#d12f3c] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <Label htmlFor="title" className="text-gray-300">Title</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="The Art of Selling to..." className="mt-1.5 bg-[#161618] border-[#2a2a2e] text-white text-lg" />
        </div>
        <div>
          <Label htmlFor="subtitle" className="text-gray-300">Subtitle</Label>
          <Input id="subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Introduction" className="mt-1.5 bg-[#161618] border-[#2a2a2e] text-white" />
        </div>

        {/* Feature image */}
        <div>
          <Label className="text-gray-300">Feature image</Label>
          <div className="mt-1.5 flex items-center gap-4">
            {imageUrl ? (
              <img src={resolveImg(imageUrl)} alt="feature" className="w-28 h-20 object-cover rounded border border-[#2a2a2e]" />
            ) : (
              <div className="w-28 h-20 rounded border border-dashed border-[#2a2a2e] flex items-center justify-center text-gray-600"><ImagePlus className="w-5 h-5" /></div>
            )}
            <input ref={imgRef} type="file" accept="image/*" onChange={onImage} className="hidden" id="imgfile" />
            <Button type="button" variant="outline" onClick={() => imgRef.current?.click()} disabled={uploadingImg} className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white">
              {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> {imageUrl ? "Replace" : "Upload"} image</>}
            </Button>
            {imageUrl && <button onClick={() => setImageUrl(null)} className="text-sm text-red-400 hover:underline">Remove</button>}
          </div>
        </div>

        {/* Docx import */}
        <div className="rounded-lg border border-[#2a2a2e] bg-[#161618] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-300"><FileText className="w-4 h-4 text-[#e63946]" /> Import from Word (.docx)</div>
            <input ref={docxRef} type="file" accept=".docx" onChange={onDocx} className="hidden" id="docxfile" />
            <Button type="button" variant="outline" onClick={() => docxRef.current?.click()} disabled={converting} className="border-[#2a2a2e] bg-transparent text-gray-200 hover:bg-[#1c1c1f] hover:text-white">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Choose .docx</>}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Bold, italics, headings, lists and links are preserved. You can fine-tune below.</p>
        </div>

        {/* Editor */}
        <div>
          <Label className="text-gray-300">Content</Label>
          <div className="mt-1.5">
            <RichTextEditor value={contentHtml} onChange={setContentHtml} resetKey={resetKey} />
          </div>
        </div>

        {/* Publish toggle */}
        <div className="flex items-center justify-between rounded-lg border border-[#2a2a2e] bg-[#161618] p-4">
          <div>
            <p className="font-medium">{published ? "Published" : "Draft"}</p>
            <p className="text-xs text-gray-500">{published ? "Visible on the public site" : "Hidden from visitors"}</p>
          </div>
          <Switch checked={published} onCheckedChange={setPublished} />
        </div>
      </main>
    </div>
  );
}
